import { getUserVisibleOrDefaultExceptionMessage } from '@paragon/client-tools';
import { ComponentDesign } from '@paragon/contract-library';
import { designService } from '@paragon/data-connections';
import { componentDesignsDeleted } from '@paragon/layout-models';
import { enqueueError } from '@paragon/notification-tools';
import { addSpinnerMessage, removeSpinnerMessage } from '@paragon/task-spinner';

import { focusCameraOnSelectedObjects } from '../../three/ThreeJsSceneActions';
import { getNaturalSortedComponentDesigns } from '../selectors/getSortedComponentDesignsSelectors';
import { closeImportComponentPanel, setImportTemplateComponent } from '../slices/applicationSlice';
import { duplicateComponentDesign } from './appActions';
import { receiveComponentDesign } from './loadActions';
import { forceClearSelection, selectItem } from './selectActions';
import { AsyncThunkActionReturn, AsyncThunkActionVoid } from './thunkActions';

export const previewComponentDesignForImport =
  (templateDesignGuid: string): AsyncThunkActionReturn<ComponentDesign> =>
  async (dispatch, getState) => {
    const oldTemplateDesignGuid = getState().application.importingComponentInfo?.templateComponentGuid;

    const { componentDesign } = await designService.componentDesigns.fetch(templateDesignGuid);
    await dispatch(
      receiveComponentDesign(componentDesign, {
        commitComponentDesign: false,
        replaceWorkingChanges: true,
        isPersisted: false,
        skipJackGirderConnections: true,
      }),
    );
    dispatch(selectItem(componentDesign, 'componentDesign', { retainCameraPosition: false }));
    dispatch(setImportTemplateComponent({ componentDesignGuid: templateDesignGuid }));

    if (oldTemplateDesignGuid) {
      dispatch(componentDesignsDeleted(oldTemplateDesignGuid));
    }

    return componentDesign;
  };

export const finishComponentDesignImport =
  (componentName: string): AsyncThunkActionVoid =>
  async (dispatch, getState) => {
    const info = getState().application.importingComponentInfo;
    if (info?.templateComponentGuid == null) {
      return;
    }

    dispatch(closeImportComponentPanel());

    const { openContext } = getState().application;
    const parentGuid = info.parentGuid ?? (openContext?.type === 'project' ? openContext.projectGuid : null);

    const spinnerId = dispatch(addSpinnerMessage('Importing component design...'));

    let duplicatedComponentDesign: ComponentDesign;
    try {
      duplicatedComponentDesign = await dispatch(
        duplicateComponentDesign(info.templateComponentGuid, {
          componentName,
          duplicateSettings: false,
          duplicateJackConnections: false,
          parentGuid,
        }),
      );

      dispatch(selectItem(duplicatedComponentDesign, 'componentDesign'));
      focusCameraOnSelectedObjects();
    } catch (error) {
      console.error(error);
      dispatch(enqueueError(getUserVisibleOrDefaultExceptionMessage(error, 'Error importing component design.')));
    } finally {
      dispatch(removeSpinnerMessage(spinnerId));
      dispatch(componentDesignsDeleted(info.templateComponentGuid));
    }
  };

export const cancelComponentDesignImport =
  (componentDesignGuid: string): AsyncThunkActionVoid =>
  async (dispatch, getState) => {
    dispatch(closeImportComponentPanel());
    dispatch(componentDesignsDeleted(componentDesignGuid));

    const naturalSortedComponentDesigns = getNaturalSortedComponentDesigns(getState());
    if (naturalSortedComponentDesigns.length > 0)
      dispatch(selectItem(naturalSortedComponentDesigns[0], 'componentDesign'));
    else dispatch(forceClearSelection());
  };
